import { PaxPrice, PriceDetailPromo, Promo } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { ReadonlyNonEmptyArray } from "fp-ts/ReadonlyNonEmptyArray"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import * as O from "fp-ts/Option"
import { bulletRow, paxRow, ReceiptRowBullet, ReceiptSectionWithLabel } from "../types"
import {
    emptyRows,
    groupPriceSummaryRowDataByLabel,
    guestLabel,
    isDiscount,
    removeRowsIfAllHaveSameAmount,
    vsCodeLabel,
} from "./utils"
import { prefixedLabelFor, PromoLabels } from "./promos"

const vsSalesPromoCodes: Promo[] = ["VS" as const, "VSMembership" as const]
export const isVsSalesPromo = (promo: PriceDetailPromo): boolean => vsSalesPromoCodes.includes(promo.promo)

export const summaryForVSSailingPromotions = (
    paxPrices: readonly PaxPrice[],
    promoLabels: PromoLabels,
    vsCode?: string,
): readonly ReceiptSectionWithLabel[] =>
    F.pipe(
        paxPrices,
        ROA.chain(vsSalesPromotionsToReceiptSections(promoLabels)),
        ROA.match(
            emptyRows,
            F.flow(
                groupPriceSummaryRowDataByLabel,
                RONEA.map(removeRowsIfAllHaveSameAmount),
                toVenetianSocietySection(vsCode),
            ),
        ),
    )

const vsSalesPromotionsToReceiptSections =
    (promoLabels: PromoLabels) =>
    (paxPrice: PaxPrice): readonly ReceiptRowBullet[] =>
        F.pipe(
            paxPrice.details.promotions,
            ROA.filter(isVsSalesPromo),
            ROA.map((promo) => toBulletRow(paxPrice.number, promo, promoLabels)),
        )

const toBulletRow = (paxNumber: number, promo: PriceDetailPromo, promoLabels: PromoLabels): ReceiptRowBullet =>
    bulletRow({
        label: prefixedLabelFor(promo, promoLabels, []),
        isPromotional: isDiscount(promo.discount),
        amount: O.some(promo.discount),
        rows: [
            paxRow({
                label: guestLabel(paxNumber),
                isPromotional: isDiscount(promo.discount),
                amount: O.some(promo.discount),
            }),
        ],
    })

const toVenetianSocietySection =
    (vsCode?: string) =>
    (rows: ReadonlyNonEmptyArray<ReceiptRowBullet>): readonly ReceiptSectionWithLabel[] => [
        {
            label: i18n._(t`Venetian Society membership`),
            key: "Venetian Society membership",
            note: vsCode ? vsCodeLabel(vsCode) : undefined,
            totalAmount: O.none,
            isPromotional: true,
            rows: F.pipe(rows, RONEA.map(dontShowAmountIfHasRows)),
        },
    ]

const dontShowAmountIfHasRows = (rowBullet: ReceiptRowBullet) => {
    if (rowBullet.rows.length > 0) return { ...rowBullet, amount: O.none }
    return rowBullet
}
